import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import Head from "next/head";
import dynamic from "next/dynamic";
import axios from "axios";
import { getCookie } from "utils/index";
import Herosection from "@/components/services/serviceherosection";
import WelcomeSection from "@/components/services/WelcomeSection";
import React from "react";
import { set } from "date-fns";
import logo1 from "../../public/1.webp";
import logo2 from "../../public/2.webp";
//import logo3 from "../../public/3.webp";
import logo4 from "../../public/4.webp";

const Skeleton = dynamic(() => import("react-loading-skeleton"), {
  ssr: false,
});

const metadata = {
  title: "NEET Support | Best NEET Counselling Services at affordabale prices",
  description:
    "Get NEET Support services for personalized counselling, college predictions, rank analysis, and admission guidance. Simplify your NEET journey today",
  keywords:
    "personalized counselling, college predictor, rank predictor, college list",
  canonical: "https://neetsupport.com/services",
  robots: "index, follow",
};

const pricingTiersUG = ["free", "basic", "premium"];
const pricingTiersPG = ["pgfree", "pgbasic", "pgpremium"];

const checkIcon = <span className="text-green-500 text-[15px]">✔</span>;
const crossIcon = <span className="text-red-500 text-[15px]">✖</span>;

const insertSpacesInCamelCase = (str) => {
  return str.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/&/g, " & ");
};

const insertSoftHyphen = (feature) => {
  if (feature.length > 10) {
    return feature
      .split("")
      .map((char, index) =>
        index !== 0 && index % 10 === 0 ? `${char}\u00AD` : char
      )
      .join("");
  }
  return feature;
};

const renderFeatureValue = (value) => (
  <div className="flex justify-center items-center space-x-2">
    {value === "Yes" ? checkIcon : crossIcon}
  </div>
);

// For PG, show string value or Yes/No icons if value is "Yes"/"No"
const renderPGFeatureValue = (value) => {
  if (value === "Yes") {
    return <span className="text-green-500 text-[15px]">✔</span>;
  }
  if (value === "No") {
    return <span className="text-red-500 text-[15px]">✖</span>;
  }
  return (
    <span className="text-xs md:text-sm sm:text-base text-[#140342] font-medium">
      {value ? value : <span className="text-gray-400">-</span>}
    </span>
  );
};

const tierLogos = {
  free: logo1,
  basic: logo2,
  //standard: logo3,
  premium: logo4,
};

// ✅ This is base price (excl. GST) - as per your modal breakdown style
const UG_PREMIUM_OLD_PRICE = 33880;

/* =========================
   ✅ NEW: Pricing Info Helpers + Card
========================= */

const GST_RATE = 0.18;

const formatINR = (n) => {
  const num = Number(n) || 0;
  return new Intl.NumberFormat("en-IN").format(Math.round(num));
};

const calcGstBreakdownFromBase = (baseExclGst) => {
  const base = Math.round(Number(baseExclGst) || 0);
  const gst = Math.round(base * GST_RATE);
  const total = base + gst;
  return { base, gst, total };
};

function InfoIcon({ className = "" }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M12 10v6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M12 7h.01"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Row({
  label,
  value,
  strong = false,
  valueClass = "text-gray-800",
  labelClass = "text-gray-500",
}) {
  return (
    <div className="flex items-center justify-between">
      <div className={`text-sm ${labelClass} ${strong ? "font-semibold" : ""}`}>
        {label}
      </div>
      <div className={`text-sm ${valueClass} ${strong ? "font-semibold" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function PricingInfoCard({
  title = "Pricing Details",
  originalBase,
  offerBase,
  onClose,
}) {
  const original = calcGstBreakdownFromBase(originalBase);
  const offer = calcGstBreakdownFromBase(offerBase);
  const discount = Math.max(0, original.base - offer.base);

  return (
    <div className="absolute right-0 top-10 z-50 w-[320px] sm:w-[360px] bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="font-semibold text-gray-800 text-sm">{title}</div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-500 hover:text-gray-800 text-lg leading-none"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-3">
        {/* Original */}
        <div className="space-y-2">
          <Row label="Original Price" value={`₹${formatINR(original.base)}`} />
          <Row label="GST" value={`₹${formatINR(original.gst)}`} />
          <Row
            label="Total (Incl. of GST)"
            value={
              <span className="line-through">₹{formatINR(original.total)}</span>
            }
            strong
          />
        </div>

        <div className="h-px bg-gray-100" />

        {/* Offer */}
        <div className="space-y-2">
          <Row
            label="Discount"
            value={`- ₹${formatINR(discount)}`}
            valueClass="text-green-600"
          />
          <Row label="Offer Price" value={`₹${formatINR(offer.base)}`} />
          <Row label="GST" value={`₹${formatINR(offer.gst)}`} />
          <Row
            label="Total (Incl. of GST)"
            value={`₹${formatINR(offer.total)}`}
            strong
            valueClass="text-green-700"
            labelClass="text-green-700"
          />
        </div>
      </div>
    </div>
  );
}

const ServicePage = ({
  authToken,
  pricingData,
  pricingDataPG,
  userTier,
  faqData,
}) => {
  const [activeFAQ, setActiveFAQ] = useState(null);
  const [selectedTab, setSelectedTab] = useState("UG");
  const router = useRouter();
  const [error, setError] = useState(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isTableVisible, setIsTableVisible] = useState(true);
  const tableRef = useRef(null);

  // ✅ NEW: pricing info popup control
  const [showPriceInfo, setShowPriceInfo] = useState(false);
  const priceInfoRef = useRef(null);

  const isLoggedIn = !!authToken;
  const userPlan = userTier;

  const handleGetStarted = (selectedTier) => {
    if (!isLoggedIn) {
      router.push("/login");
      return;
    }

    const isPG = selectedTab === "PG";
    const selectedPricingData = isPG ? pricingDataPG : pricingData;

    if (!selectedPricingData || !selectedPricingData.features) {
      console.error("Pricing data is not available.");
      return;
    }

    // No need to map tiers anymore since they're already pgfree, pgbasic, pgpremium
    const tierForCheckout = selectedTier;

    const selectedFeatures = Object.keys(selectedPricingData.features).flatMap(
      (section) => {
        const sectionFeatures = selectedPricingData.features[section];
        if (!sectionFeatures) return [];
        return Object.keys(sectionFeatures).filter(
          (feature) => sectionFeatures[feature]?.[selectedTier] === "Yes"
        );
      }
    );

    // ✅ For UG Premium, calculate total price including GST
    let priceToPass = selectedPricingData?.priceDetails?.[selectedTier] || 0;
    if (!isPG && selectedTier === "premium") {
      const breakdown = calcGstBreakdownFromBase(priceToPass);
      priceToPass = breakdown.total;
    }

    const query = {
      tier: tierForCheckout,
      features: JSON.stringify(selectedFeatures),
      price: priceToPass,
      priceDetails: JSON.stringify(selectedPricingData?.priceDetails || {}),
    };

    router.push(
      {
        pathname: "/checkout",
        query: query,
      },
      undefined,
      { shallow: false }
    );
  };

  const toggleFAQ = (index) => {
    setActiveFAQ((prev) => (prev === index ? null : index));
  };

  useEffect(() => {
    const handleScroll = () => {
      const tableBottom = tableRef.current?.getBoundingClientRect().bottom;
      setIsSticky(window.scrollY > 100);
      setIsTableVisible(tableBottom > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // ✅ NEW: close popup on outside click
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!showPriceInfo) return;
      if (priceInfoRef.current && !priceInfoRef.current.contains(e.target)) {
        setShowPriceInfo(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [showPriceInfo]);

  // For mobile, always use 3 columns for both UG and PG
  const pricingTiers =
    selectedTab === "UG" ? pricingTiersUG.slice(0, 3) : pricingTiersPG;
  const activePricingData =
    selectedTab === "UG" ? pricingData : pricingDataPG;

  if (error) {
    return <div>Error fetching pricing data: {error}</div>;
  }

  if (!activePricingData) {
    return <div>No pricing data available</div>;
  }

  // ✅ Offer base price from DB (UG Premium)
  const ugPremiumOfferBase = Number(activePricingData?.priceDetails?.premium || 0);

  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta charSet="utf-8" />
        <link rel="canonical" href={metadata.canonical} />
        <meta name="robots" content={metadata.robots} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "NEET Support",
            "url": "https://neetsupport.com",
            "logo": "https://neetsupport.com/favicon/neetsupport-logo.webp",
            "image": "https://neetsupport.com/assets/images/services-img/Tools.webp",
            "description": "NEET Support provides comprehensive counseling services for NEET aspirants, including rank prediction, college selection guidance, and personalized mentorship.",
            "telephone": "+91 95604 65351",
            "priceRange": "₹0 - ₹39,980",
            "address": [
      {
        "@type": "PostalAddress",
        "name": "Head Office",
        "streetAddress": "Ground Floor, C-127, C Block, Sector 63",
        "addressLocality": "Noida",
        "addressRegion": "Uttar Pradesh",
        "postalCode": "201301",
        "addressCountry": "IN"
      },
      
    ],
            "contactPoint": [
              {
                "@type": "ContactPoint",
                "telephone": "+91 95604 65351",
                "contactType": "Customer Service",
                "email": "info@neetsupport.com",
                "areaServed": {
                  "@type": "Country",
                  "name": "India"
                }
              }
            ],
            "sameAs": [
              "https://www.facebook.com/NEETSupport.comIND",
              "https://www.instagram.com/neetsupportindia/",
              "https://www.linkedin.com/company/neetsupport/",
              "https://www.youtube.com/@NEETSupportIndia"
            ],
            "hasOfferCatalog": 
            {
              "@type": "OfferCatalog",
              "name": "Counseling Services Packages",
              "itemListElement": [
                {
                  "@type": "Service",
                  "name": "Free Package",
                  "serviceType": "Counseling Services",
                  "description": "Includes Rank Predictor and Exam Paper analysis.",
                  "offers": {
                    "@type": "Offer",
                    "price": "0",
                    "priceCurrency": "INR"
                  }
                },
                {
                  "@type": "Service",
                  "name": "Basic Package",
                  "serviceType": "Counseling Services",
                  "description": "Includes NEET Score Booster, Fees Seat Matrix, Cut Off Allotment, College Compare, Admission Committee access, News Alerts, College Search, Merit List, Seat Matrix, Cut Off analysis, Free Webinar, Domicile State Counseling Ebook, Exclusive Webinar Workshops, Counseling Videos, and Medical Guide Ebook.",
                  "offers": {
                    "@type": "Offer",
                    "price": "4980",
                    "priceCurrency": "INR"
                  }
                },
                {
                  "@type": "Service",
                  "name": "Premium Package",
                  "serviceType": "Counseling Services",
                  "description": "Includes all features, personalized mentorship, and dedicated counseling support.",
                  "offers": {
                    "@type": "Offer",
                    "price": "34980",
                    "priceCurrency": "INR"
                  }
                }
              ]
            }
          
          }
      `,
          }}
        />
        {/* Add FAQPage JSON-LD schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `
{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How does NEET Support help with college predictions?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "NEET Support helps with college predictions using its College Predictor Tool, which shows you the MBBS/BDS colleges you may get based on your NEET rank, category, state, and quota. It uses real cut-off data from previous years to give accurate and personalized results, helping you plan your counselling confidently and save time."
      }
    },
    {
      "@type": "Question",
      "name": "Can NEET Support assist with document validation?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, NEET Support assists with document validation by helping students check and verify all necessary documents required for NEET counselling. This includes Domicile Certificate, Category/Reservation Proof, NEET Admit Card & Scorecard, ID Proofs, Academic Records, and NRI/Management Quota Documents (if applicable)."
      }
    },
    {
      "@type": "Question",
      "name": "What kind of personalized guidance does NEET Support provide?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "NEET Support offers personalized guidance such as one-on-one counselling, college shortlisting, choice filling support, expert advice for AIQ and state strategies, and guidance for special quotas like NRI or reserved categories."
      }
    },
    {
      "@type": "Question",
      "name": "How does NEET Support simplify the NEET counselling process?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "NEET Support simplifies the counselling process with college prediction tools, personalized support, alerts and reminders, document validation, expert counselling, and real-time updates, helping students make informed and confident decisions."
      }
    },
    {
      "@type": "Question",
      "name": "How does NEET Support handle counselling for both UG and PG medical courses?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For UG, NEET Support offers help with college prediction, quota-based counselling, and document support. For PG, it includes PG college prediction, AIQ and state-level guidance, and information on bonds, stipends, and seat matrix."
      }
    },
    {
      "@type": "Question",
      "name": "What are the main benefits of using NEET Support over other counselling services?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "NEET Support offers accurate college predictions, personalized guidance, support for all quotas, step-by-step help with documents and choice filling, affordable plans, expert mentors, and tools like college compare and fee matrix."
      }
    },
    {
      "@type": "Question",
      "name": "How does NEET Support ensure that students avoid common counselling pitfalls?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "NEET Support prevents common mistakes by offering accurate choice filling, alerts for deadlines, document verification, understanding of seat rules, and expert review of preferences before final submission."
      }
    },
    {
      "@type": "Question",
      "name": "What kind of support does NEET Support offer during the counselling process?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "NEET Support provides full assistance including document validation, personalized college shortlist, expert choice filling guidance, registration help, alerts, and one-on-one counselling via call, chat, or video."
      }
    },
    {
      "@type": "Question",
      "name": "How NEET Support Uses AI in Counselling Services?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "NEET Support uses AI-powered tools like College Predictor, Rank Analysis, Smart Choice Filling, and Real-Time Filters to offer accurate suggestions and increase your chances of successful seat allotment."
      }
    }
  ]
}
            `,
          }}
        />
      </Head>

      <div className="mt-[80px] overflow-x-hidden">{/* <Herosection /> */}</div>

      <div className="min-h-screen bg-white mt-[20px] p-4 overflow-x-hidden">
        <header className="text-center py-[10px] bg-white">
          <h1 className="sm:text-3xl text-xl font-bold text-[#140342]">
            Best NEET Counselling Services,{" "}
            <span className="text-[#ff9245]">
              Transparent & Affordable Pricing.
            </span>
          </h1>

          <p className="text-gray-700 sm:text-base text-sm font-semibold mt-2">
            100% Transparency | Budget-Friendly Plans | Complete NEET
            Counselling Support
          </p>
        </header>

        {/* UG/PG Toggle */}
        <div className="flex justify-center items-center mt-6 mb-4 ">
          <div className="inline-flex items-center bg-[#ff9245] rounded-full p-1 ">
            <button
              className={`px-10 py-2 rounded-full transition-all duration-300 font-bold text-md ${selectedTab === "UG"
                  ? "bg-white text-[#ff9245] shadow"
                  : "text-white"
                }`}
              onClick={() => setSelectedTab("UG")}
            >
              UG
            </button>
            <button
              className={`px-10 py-2 rounded-full transition-all duration-300 font-bold text-md ${selectedTab === "PG"
                  ? "bg-white text-[#ff9245] shadow"
                  : "text-white"
                }`}
              onClick={() => setSelectedTab("PG")}
            >
              PG
            </button>
          </div>
        </div>

        {/* Pricing Table */}
        <section className="w-full max-w-7xl mx-auto border border-grey-500 rounded-lg p-2 md:p-6 mt-6 overflow-x-auto">
          <div className="hidden md:block">
            <div className="overflow-x-auto">
              {/* Desktop Table Header */}
              {selectedTab === "PG" ? (
                /* --- PG HEADER --- */
                <div
                  className="min-w-[600px] grid items-center bg-[#FEF4ED] font-semibold text-teal-900 rounded-tr-3xl rounded-tl-3xl"
                  style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}
                >
                  <span className="text-left font-bold pl-4 pr-4 sm:text-lg text-[#ff9245] border-r-2 border-[#ff9245] rounded-tl-3xl"></span>

                  {/* FREE */}
                  <div className="text-center flex flex-col items-center py-4 border-r-2 border-l-2 border-[#ff9245]">
                    <img
                      src={tierLogos["free"]?.src}
                      alt="free logo"
                      className="w-10 h-10 mb-2"
                    />
                    <h2 className="capitalize text-xs md:text-sm sm:text-base">
                      FREE
                    </h2>
                    <p className="font-bold text-xs md:text-sm sm:text-base">
                      ₹{activePricingData.priceDetails["pgfree"]}/-{" "}
                      <span className="text-[9px] sm:text-xs text-gray-500">
                        (Incl. GST)
                      </span>
                    </p>
                    <button
                      onClick={() => handleGetStarted("pgfree")}
                      disabled={
                        userTier === "pgfree" ||
                        userTier === "pgbasic" ||
                        userTier === "pgpremium"
                      }
                      className={`inline-block mt-[7.5px] px-8 py-2 sm:px-5 sm:py-2 text-xs md:text-sm sm:text-base rounded-md transition duration-300 ease-in-out ${userTier === "pgfree" ||
                          userTier === "pgbasic" ||
                          userTier === "pgpremium"
                          ? "text-gray-400 border-gray-400 cursor-not-allowed"
                          : "text-[#ff9245] border-[#ff9245] hover:bg-[#ff9245] hover:text-white"
                        }`}
                      style={{
                        border: "1px solid #ff9245",
                        width: "fit-content",
                        fontSize: "13.125px",
                        padding: "8px 7.5px",
                      }}
                    >
                      {userTier === "pgfree"
                        ? "Active Plan"
                        : userTier === "pgbasic" || userTier === "pgpremium"
                          ? "Enrolled"
                          : "Get started"}
                    </button>
                  </div>

                  {/* BASIC */}
                  <div className="text-center flex flex-col items-center py-4 border-r-2 border-[#ff9245]">
                    <img
                      src={tierLogos["basic"]?.src}
                      alt="basic logo"
                      className="w-10 h-10 mb-2"
                    />
                    <h2 className="capitalize text-xs md:text-sm sm:text-base">
                      BASIC
                    </h2>
                    <p className="font-bold text-xs md:text-sm sm:text-base">
                      ₹{activePricingData.priceDetails["pgbasic"]}/-{" "}
                      <span className="text-[9px] sm:text-xs text-gray-500">
                        (Incl. GST)
                      </span>
                    </p>
                    <button
                      onClick={() => handleGetStarted("pgbasic")}
                      disabled={userTier === "pgbasic" || userTier === "pgpremium"}
                      className={`inline-block mt-[7.5px] px-8 py-2 sm:px-5 sm:py-2 text-xs md:text-sm sm:text-base rounded-md transition duration-300 ease-in-out ${userTier === "pgbasic" || userTier === "pgpremium"
                          ? "text-gray-400 border-gray-400 cursor-not-allowed"
                          : "text-[#ff9245] border-[#ff9245] hover:bg-[#ff9245] hover:text-white"
                        }`}
                      style={{
                        border: "1px solid #ff9245",
                        width: "fit-content",
                        fontSize: "13.125px",
                        padding: "8px 7.5px",
                      }}
                    >
                      {userTier === "pgbasic"
                        ? "Active Plan"
                        : userTier === "pgpremium"
                          ? "Enrolled"
                          : "Get started"}
                    </button>
                  </div>

                  {/* PREMIUM */}
                  <div className="text-center flex flex-col items-center py-4 border-r-2 border-[#ff9245] rounded-tr-3xl">
                    <img
                      src={tierLogos["premium"]?.src}
                      alt="premium logo"
                      className="w-10 h-10 mb-2"
                    />
                    <h2 className="capitalize text-xs md:text-sm sm:text-base">
                      PREMIUM
                    </h2>
                    <p className="font-bold text-xs md:text-sm sm:text-base">
                      ₹{activePricingData.priceDetails["pgpremium"]}/-{" "}
                      <span className="text-[9px] sm:text-xs text-gray-500">
                        (Incl. GST)
                      </span>
                    </p>
                    <button
                      onClick={() => handleGetStarted("pgpremium")}
                      disabled={userTier === "pgpremium"}
                      className={`inline-block mt-[7.5px] px-8 py-2 sm:px-5 sm:py-2 text-xs md:text-sm sm:text-base rounded-md transition duration-300 ease-in-out ${userTier === "pgpremium"
                          ? "text-gray-400 border-gray-400 cursor-not-allowed"
                          : "text-[#ff9245] border-[#ff9245] hover:bg-[#ff9245] hover:text-white"
                        }`}
                      style={{
                        border: "1px solid #ff9245",
                        width: "fit-content",
                        fontSize: "13.125px",
                        padding: "8px 7.5px",
                      }}
                    >
                      {userTier === "pgpremium" ? "Active Plan" : "Get started"}
                    </button>
                  </div>
                </div>
              ) : (
                /* --- UG HEADER --- */
                <div
                  className={`min-w-[600px] grid grid-cols-4 items-center bg-[#FEF4ED] font-semibold text-teal-900 rounded-tr-3xl `}
                >
                  <span className="text-left font-bold pl-4 pr-4 sm:text-lg text-[#ff9245] "></span>

                  {pricingTiers.map((tier, index) => {
                    const userTierIndex = pricingTiers.indexOf(userTier);
                    const tierIndex = pricingTiers.indexOf(tier);
                    const shouldDisable =
                      userTier === null
                        ? false
                        : userTierIndex === 3 ||
                        (userTierIndex === 2 && tierIndex <= 1) ||
                        (userTierIndex === 1 && tierIndex === 0) ||
                        (userTierIndex === 0 && tierIndex === 0);

                    return (
                      <div
                        key={index}
                        className={`text-center flex flex-col items-center py-4 border-r-2 ${index === 0 ? "border-l-2" : ""
                          } border-[#ff9245] ${index === pricingTiers.length - 1 ? "rounded-tr-3xl" : ""
                          }`}
                      >
                        {tier && (
                          <img
                            src={tierLogos[tier]?.src}
                            alt={`${tier} logo`}
                            className="w-10 h-10 mb-2"
                          />
                        )}

                        <h2 className="capitalize text-xs md:text-sm sm:text-base">
                          {tier === "free" ? "FREE" : tier.toUpperCase()}
                        </h2>

                        {/* ✅ UPDATED PREMIUM PRICE UI: old strike + new + "-GST" + info icon */}
                        {tier === "premium" ? (
                          <div className="flex flex-col items-center">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400 line-through text-xs sm:text-sm">
                                ₹{formatINR(UG_PREMIUM_OLD_PRICE)}/-
                              </span>
                              <span className="text-gray-900 font-extrabold text-base sm:text-lg">
                                ₹{formatINR(ugPremiumOfferBase)}/-
                              </span>
                              <span className="text-gray-500 font-semibold text-xs">
                                +GST
                              </span>

                              <div className="relative" ref={priceInfoRef}>
                                <button
                                  type="button"
                                  onClick={() => setShowPriceInfo((v) => !v)}
                                  className="text-gray-400 hover:text-gray-700"
                                  aria-label="Pricing details"
                                >
                                  <InfoIcon className="w-4 h-4" />
                                </button>

                                {showPriceInfo && (
                                  <PricingInfoCard
                                    title="Pricing Details - UG Premium"
                                    originalBase={UG_PREMIUM_OLD_PRICE}
                                    offerBase={ugPremiumOfferBase} // ✅ from DB
                                    onClose={() => setShowPriceInfo(false)}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <p className="font-bold text-xs md:text-sm sm:text-base">
                            ₹{activePricingData.priceDetails[tier]}/-{" "}
                            <span className="text-[9px] sm:text-xs text-gray-500">
                              (Incl. GST)
                            </span>
                          </p>
                        )}

                        <button
                          onClick={() => handleGetStarted(tier)}
                          disabled={shouldDisable || tier === userTier}
                          className={`inline-block mt-[7.5px] px-8 py-2 sm:px-5 sm:py-2 text-xs md:text-sm sm:text-base rounded-md transition duration-300 ease-in-out 
              ${shouldDisable || tier === userTier
                              ? "text-gray-400 border-gray-400 cursor-not-allowed"
                              : "text-[#ff9245] border-[#ff9245] hover:bg-[#ff9245] hover:text-white"
                            }
              `}
                          style={{
                            border: "1px solid #ff9245",
                            width: "fit-content",
                            fontSize: "13.125px",
                            padding: "8px 7.5px",
                          }}
                        >
                          {tier === userTier
                            ? "Active Plan"
                            : shouldDisable
                              ? "Enrolled"
                              : "Get started"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Desktop Features Section */}
              {selectedTab === "PG" ? (
                /* --- PG TABLE --- */
                <div className="min-w-[600px]">
                  {(() => {
                    // Get all section keys except lastUpdatedBy, _id, and ServicesYear
                    const allSections = Object.keys(activePricingData.features)
                      .filter(
                        (section) =>
                          section !== "lastUpdatedBy" &&
                          section !== "_id" &&
                          section !== "ServicesYear"
                      );
                    // Sort as before
                    allSections.sort((a, b) => {
                      const order = ["tools", "analysis", "postExamMentorship"];
                      return order.indexOf(a) - order.indexOf(b);
                    });
                    // Add ServicesYear at the end if it exists
                    if (activePricingData.features.ServicesYear) {
                      allSections.push("ServicesYear");
                    }
                    return allSections.map((section, sectionIndex) => (
                      <div key={sectionIndex}>
                        <div
                          className="grid items-center h-full"
                          style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}
                        >
                          <div className="mt-6 ">
                            <h2 className="text-lg text-left ml-[7.5px] font-semibold text-[#ff9245] capitalize mb-[7.5px] px-[15px]">
                              {insertSpacesInCamelCase(section)}
                            </h2>
                          </div>
                          <div className="text-center py-[7.5px] h-full border-r-2 border-l-2 border-[#ff9245]" />
                          <div className="text-center py-[7.5px] h-full border-r-2 border-[#ff9245]" />
                          <div className="h-full border-r-2 border-[#ff9245]" />
                        </div>
                        <div className="overflow-x-auto">
                          {Object.keys(activePricingData.features[section]).map(
                            (feature, idx) => (
                              <div
                                key={idx}
                                className={`grid items-center ${idx % 2 === 0 ? "bg-orange-50" : "bg-white"
                                  }`}
                                style={{
                                  gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                }}
                              >
                                <span className="text-left font-medium py-[7.5px] pl-2 capitalize text-sm sm:text-base whitespace-normal text-[#4F547B] h-full ">
                                  {insertSpacesInCamelCase(feature)}
                                </span>
                                {pricingTiersPG.map((tier, tierIdx) => (
                                  <span
                                    key={tierIdx}
                                    className={`text-center py-[7.5px] h-full border-r-2 ${tierIdx === 0 ? "border-l-2" : ""
                                      } border-[#ff9245]`}
                                  >
                                    {renderPGFeatureValue(
                                      activePricingData.features[section][feature][tier]
                                    )}
                                  </span>
                                ))}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    ));
                  })()}
                  <div
                    className="grid items-center"
                    style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}
                  >
                    <div className="min-h-[36px] border-r-2 border-[#ff9245] rounded-br-3xl mr-[-2px]" />
                    <div className="min-h-[36px] border-r-2 border- border-[#ff9245] rounded-br-3xl" />
                    <div className="min-h-[36px] border-r-2 border-[#ff9245] rounded-br-3xl" />
                    <div className="min-h-[36px] border-r-2 border-[#ff9245] rounded-br-3xl" />
                  </div>
                </div>
              ) : (
                /* --- UG TABLE --- */
                <div className="min-w-[600px]">
                  {Object.keys(pricingData.features)
                    .filter(
                      (section) =>
                        section !== "lastUpdatedBy" &&
                        section !== "_id"
                    )
                    .map((section, sectionIndex) => (
                      <div key={sectionIndex} className="">
                        <div
                          className={`grid grid-cols-${pricingTiers.length + 1
                            } items-center`}
                        >
                          <div className="mt-6">
                            <h2 className="text-lg text-left ml-[7.5px] font-semibold text-[#ff9245] capitalize mb-[7.5px] px-[15px] ">
                              {insertSpacesInCamelCase(section)}
                            </h2>
                          </div>
                          {pricingTiers.map((tier, idx) => (
                            <div
                              key={tier}
                              className={`h-full border-r-2 ${idx === 0 ? "border-l-2" : ""
                                } border-[#ff9245]`}
                            />
                          ))}
                        </div>

                        <div className="overflow-x-auto">
                          {Object.keys(pricingData.features[section]).map(
                            (feature, idx) => (
                              <div
                                key={idx}
                                className={`grid grid-cols-${pricingTiers.length + 1
                                  } items-center ${idx % 2 === 0 ? "bg-orange-50" : "bg-white"
                                  }`}
                              >
                                <span className="text-left font-medium py-[7.5px] pl-2 capitalize text-sm sm:text-base whitespace-normal text-[#4F547B] ">
                                  {insertSoftHyphen(
                                    insertSpacesInCamelCase(feature)
                                  )}
                                </span>
                                {pricingTiers.map((tier, index) => (
                                  <span
                                    key={tier}
                                    className={`text-center py-[7.5px] h-full border-r-2 ${index === 0 ? "border-l-2" : ""
                                      } border-[#ff9245]`}
                                  >
                                    {renderFeatureValue(
                                      pricingData.features[section][feature][tier]
                                    )}
                                  </span>
                                ))}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    ))}
                  <div
                    className={`grid grid-cols-${pricingTiers.length + 1
                      } items-center `}
                  >
                    <div className="min-h-[36px] border-r-2 border-[#ff9245] rounded-br-3xl mr-[-2px]">
                      <h2 className="text-lg text-left ml-[7.5px] font-semibold text-[#ff9245] capitalize mb-[7.5px] px-[15px] "></h2>
                    </div>
                    {pricingTiers.map((tier, idx) => (
                      <div
                        key={`bottom-${tier}`}
                        className={`min-h-[36px] border-r-2 border-[#ff9245] rounded-br-3xl`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-[20px] text-left">
              <p className="text-red-500 text-sm font-semibold text-[13.125px]">
                Please note: Once payment is made, it is non-refundable.
              </p>
            </div>
          </div>

          {/* Mobile View */}
          <div className="block md:hidden">
            <table
              ref={tableRef}
              className="w-full items-center border-separate"
              style={{ borderSpacing: 0 }}
            >
              <thead
                className={`${isSticky && isTableVisible
                    ? "fixed top-[82px] right-[5px] bg-[#FFF1E5] z-10 shadow-md"
                    : ""
                  }`}
                style={{ width: "100%" }}
              >
                <tr className="bg-[#FEF4ED]">
                  {pricingTiers.map((tier, index) => {
                    const userTierIndex = pricingTiers.indexOf(userTier);
                    const tierIndex = pricingTiers.indexOf(tier);
                    const shouldDisable =
                      userTier === null
                        ? false
                        : selectedTab === "UG"
                          ? userTierIndex === 3 ||
                          (userTierIndex === 2 && tierIndex <= 1) ||
                          (userTierIndex === 1 && tierIndex === 0) ||
                          (userTierIndex === 0 && tierIndex === 0)
                          : userTierIndex >= tierIndex;

                    const displayTierName =
                      selectedTab === "PG"
                        ? tier.replace("pg", "").toUpperCase()
                        : tier.toUpperCase();

                    return (
                      <th
                        key={index}
                        className={`text-center py-3 border-r-2 border-[#ff9245] align-bottom w-1/3`}
                        style={{
                          width: "33.33%",
                          minWidth: "140px",
                          textAlign: "center",
                        }}
                      >
                        {tier && (
                          <img
                            src={tierLogos[tier.replace("pg", "")]?.src}
                            alt={`${tier} logo`}
                            className="w-8 h-8 mx-auto mb-1"
                          />
                        )}

                        <div className="text-sm font-semibold text-[#ff9245]">
                          {displayTierName}
                        </div>

                        {/* ✅ MOBILE premium price look */}
                        {selectedTab === "UG" && tier === "premium" ? (
                          <div className="flex flex-col items-center">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400 line-through text-xs">
                                ₹{formatINR(UG_PREMIUM_OLD_PRICE)}/-
                              </span>
                              <span className="text-gray-900 font-extrabold text-sm">
                                ₹{formatINR(ugPremiumOfferBase)}/-
                              </span>
                              <span className="text-gray-500 font-semibold text-[10px]">
                                -GST
                              </span>

                              <div className="relative" ref={priceInfoRef}>
                                <button
                                  type="button"
                                  onClick={() => setShowPriceInfo((v) => !v)}
                                  className="text-gray-400 hover:text-gray-700"
                                  aria-label="Pricing details"
                                >
                                  <InfoIcon className="w-4 h-4" />
                                </button>

                                {showPriceInfo && (
                                  <PricingInfoCard
                                    title="Pricing Details - UG Premium"
                                    originalBase={UG_PREMIUM_OLD_PRICE}
                                    offerBase={ugPremiumOfferBase}
                                    onClose={() => setShowPriceInfo(false)}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm font-medium text-gray-700">
                            {selectedTab === "UG" && tier === "premium" ? (
                              <>
                                <span className="text-gray-400 line-through mr-1">
                                  ₹{UG_PREMIUM_OLD_PRICE}/-
                                </span>
                                ₹{activePricingData.priceDetails[tier]}/-
                              </>
                            ) : (
                              <>₹{activePricingData.priceDetails[tier]}/-</>
                            )}
                            <span className="text-xs text-gray-500">
                              {" "}
                              (Incl. GST)
                            </span>
                          </div>
                        )}

                        <button
                          onClick={() => handleGetStarted(tier)}
                          disabled={shouldDisable || tier === userTier}
                          className={`mt-2 px-2 py-1 rounded text-sm border ${shouldDisable || tier === userTier
                              ? "bg-gray-300 cursor-not-allowed"
                              : "bg-[#ff9245] text-white"
                            }`}
                        >
                          <span className="text-xs whitespace-nowrap">
                            {userPlan === tier
                              ? "Active Plan"
                              : shouldDisable
                                ? "Enrolled"
                                : "Get started"}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {Object.keys(activePricingData.features)
                  .filter(
                    (section) =>
                      section !== "lastUpdatedBy" && section !== "_id"
                  )
                  .map((section, sectionIndex) => (
                    <React.Fragment key={sectionIndex}>
                      <tr>
                        <td
                          colSpan={pricingTiers.length}
                          className="bg-white text-[#ff9245] py-2 font-semibold text-left pl-4"
                          style={{ borderRight: "2px solid #ff9245" }}
                        >
                          {insertSpacesInCamelCase(section)
                            .charAt(0)
                            .toUpperCase() +
                            insertSpacesInCamelCase(section).slice(1)}
                        </td>
                      </tr>
                      {Object.keys(activePricingData.features[section]).map(
                        (feature, idx) => (
                          <React.Fragment key={idx}>
                            <tr>
                              <td
                                className="text-center font-medium py-2 pl-4"
                                colSpan={pricingTiers.length}
                                style={{ borderRight: "2px solid #ff9245" }}
                              >
                                {insertSpacesInCamelCase(feature)}
                              </td>
                            </tr>
                            <tr>
                              {pricingTiers.map((tier, tierIdx) => (
                                <td
                                  key={tierIdx}
                                  className="text-center py-2 border-r-2 border-[#ff9245]"
                                >
                                  {selectedTab === "PG"
                                    ? renderPGFeatureValue(
                                      activePricingData.features[section][feature][tier]
                                    )
                                    : renderFeatureValue(
                                      activePricingData.features[section][feature][tier]
                                    )}
                                </td>
                              ))}
                            </tr>
                          </React.Fragment>
                        )
                      )}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>

            <div className="mt-4 px-4">
              <p className="text-red-500 text-xs font-semibold">
                Please note: Once payment is made, it is non-refundable.
              </p>
            </div>
          </div>
        </section>

        {/* Welcome Section */}
        <WelcomeSection />

        {/* FAQ Section */}
        <section className="w-full max-w-7xl mx-auto mb-[40px] ">
          <h2 className="sm:text-3xl text-2xl font-bold text-center capitalize">
            Frequently asked <span className="text-[#ff9245]">questions</span>
          </h2>
          <div className="mt-8 space-y-4 ml-6 mr-6">
            {faqData.map((faq, index) => (
              <div
                key={index}
                className={`p-4 border rounded-lg shadow-md ${activeFAQ === index ? "bg-gray-50" : ""
                  }`}
                onClick={() => toggleFAQ(index)}
              >
                <div className="flex justify-between items-center cursor-pointer">
                  <h3
                    className="text-lg font-semibold"
                    style={{ color: "#140342", fontSize: "16.875px" }}
                  >
                    {faq.question}
                  </h3>
                  <span>{activeFAQ === index ? "-" : "+"}</span>
                </div>
                <div
                  className={`mt-2 text-gray-600 text-left text-lg ${activeFAQ === index ? "block" : "hidden"
                    }`}
                  dangerouslySetInnerHTML={{ __html: faq.answer }}
                />
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

export default ServicePage;
